import { useReducer, useCallback, useRef, useEffect } from 'react';

import type {
  CoaExpense as Expense,
  CoaGroup as SharedCoaGroup,
} from '../tuition-step.types';

type CoaGroup = SharedCoaGroup & { expenses: Expense[] };

type TuitionState = {
  coaGroups: CoaGroup[];
  initialSnapshot: CoaGroup[];
  isDirty: boolean;
};

type TuitionAction = { type: string; payload?: unknown };

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const sortExpenses = (expenses: Expense[], categoryOrder: string[]): Expense[] => {
  if (!categoryOrder.length) return expenses;

  return [...expenses].sort((a: Expense, b: Expense) => {
    const indexA = categoryOrder.indexOf(a.category ?? '');
    const indexB = categoryOrder.indexOf(b.category ?? '');

    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  });
};

const ACTIONS = {
  INIT: 'INIT',
  UPDATE_AMOUNT: 'UPDATE_AMOUNT',
  UPDATE_BY_PERCENT: 'UPDATE_BY_PERCENT',
  UPDATE_INCLUDE_FEES: 'UPDATE_INCLUDE_FEES',
  RESET: 'RESET',
};

const initialState: TuitionState = {
  coaGroups: [],
  initialSnapshot: [],
  isDirty: false,
};

const computeIsDirty = (groups: CoaGroup[], snapshot: CoaGroup[]): boolean =>
  groups.some((group: CoaGroup, gi: number) => {
    const original = snapshot[gi];

    if (!original) return true;
    if (group.includeRequiredFees !== original.includeRequiredFees) return true;

    return group.expenses.some(
      (exp: Expense, ei: number) =>
        exp.currentYearAmount !== original.expenses[ei]?.currentYearAmount
    );
  });

const withDirtyCheck = (nextState: TuitionState): TuitionState => ({
  ...nextState,
  isDirty: computeIsDirty(nextState.coaGroups, nextState.initialSnapshot),
});

const tuitionReducer = (state: TuitionState, action: TuitionAction): TuitionState => {
  switch (action.type) {
    case ACTIONS.INIT: {
      const { data, categoryOrder } = action.payload as {
        data: SharedCoaGroup[];
        categoryOrder: string[];
      };
      const sorted: CoaGroup[] = deepClone(data).map((group) => ({
        ...group,
        expenses: sortExpenses(group.expenses || [], categoryOrder),
      }));

      return {
        coaGroups: sorted,
        initialSnapshot: deepClone(sorted),
        isDirty: false,
      };
    }

    case ACTIONS.UPDATE_AMOUNT: {
      const { coaId, expenseIndex, value } = action.payload as {
        coaId: string | number;
        expenseIndex: number;
        value: unknown;
      };

      return withDirtyCheck({
        ...state,
        coaGroups: state.coaGroups.map((group: CoaGroup) => {
          if (group.id !== coaId) return group;

          return {
            ...group,
            expenses: group.expenses.map((exp: Expense, i: number) =>
              i === expenseIndex
                ? { ...exp, currentYearAmount: value }
                : exp
            ),
          };
        }),
      });
    }

    case ACTIONS.UPDATE_BY_PERCENT: {
      const { coaId, expenseIndex, percent } = action.payload as {
        coaId: string | number;
        expenseIndex: number;
        percent: number;
      };

      return withDirtyCheck({
        ...state,
        coaGroups: state.coaGroups.map((group: CoaGroup) => {
          if (group.id !== coaId) return group;

          return {
            ...group,
            expenses: group.expenses.map((exp: Expense, i: number) => {
              if (i !== expenseIndex) return exp;

              const base = Number(exp.previousYearAmount) || 0;
              const newAmount = Math.round(base * (1 + percent / 100));

              return { ...exp, currentYearAmount: newAmount };
            }),
          };
        }),
      });
    }

    case ACTIONS.UPDATE_INCLUDE_FEES: {
      const { coaId, value } = action.payload as { coaId: string | number; value: boolean };

      return withDirtyCheck({
        ...state,
        coaGroups: state.coaGroups.map((group: CoaGroup) =>
          group.id === coaId
            ? { ...group, includeRequiredFees: value }
            : group
        ),
      });
    }

    case ACTIONS.RESET:
      return tuitionReducer(state, {
        type: ACTIONS.INIT,
        payload: { data: state.initialSnapshot, categoryOrder: [] },
      });

    default:
      return state;
  }
};

export const useTuitionDraft = (
  serverData: SharedCoaGroup[] | null | undefined,
  categoryOrder: string[] = []
) => {
  const [state, dispatch] = useReducer(tuitionReducer, initialState);
  const initialized = useRef(false);

  useEffect(() => {
    if ((serverData?.length ?? 0) > 0 && !initialized.current) {
      dispatch({ type: ACTIONS.INIT, payload: { data: serverData, categoryOrder } });
      initialized.current = true;
    }
  }, [serverData, categoryOrder]);

  const updateAmount = useCallback(
    (coaId: string | number, expenseIndex: number, value: unknown) =>
      dispatch({
        type: ACTIONS.UPDATE_AMOUNT,
        payload: { coaId, expenseIndex, value },
      }),
    []
  );

  const updateByPercent = useCallback(
    (coaId: string | number, expenseIndex: number, percent: number) =>
      dispatch({
        type: ACTIONS.UPDATE_BY_PERCENT,
        payload: { coaId, expenseIndex, percent },
      }),
    []
  );

  const updateIncludeFees = useCallback(
    (coaId: string | number, value: boolean) =>
      dispatch({
        type: ACTIONS.UPDATE_INCLUDE_FEES,
        payload: { coaId, value },
      }),
    []
  );

  const reset = useCallback(() => dispatch({ type: ACTIONS.RESET }), []);

  const markForReinit = useCallback(() => {
    initialized.current = false;
  }, []);

  const getChangedGroups = useCallback(() => {
    if (!state.initialSnapshot.length) return [];

    return state.coaGroups.filter((group: CoaGroup, gi: number) => {
      const original = state.initialSnapshot[gi];

      if (!original) return true;
      if (group.includeRequiredFees !== original.includeRequiredFees) return true;

      return group.expenses.some(
        (exp: Expense, ei: number) =>
          exp.currentYearAmount !== original.expenses[ei]?.currentYearAmount
      );
    });
  }, [state]);

  return {
    coaGroups: state.coaGroups,
    hasChanges: state.isDirty,
    updateAmount,
    updateByPercent,
    updateIncludeFees,
    reset,
    markForReinit,
    getChangedGroups,
  };
};
